import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CircularProgress, Box } from '@mui/material';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import { SalesManagerRoute } from './components/common/SalesManagerRoute';


// Lazy load large components to reduce bundle size
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Customers = React.lazy(() => import('./pages/Customers'));
const Products = React.lazy(() => import('./pages/Products'));
const EtlPipelines = React.lazy(() => import('./pages/EtlPipelines'));
const PipelineBuilder = React.lazy(() => import('./pages/PipelineBuilder'));
const PipelineExecutions = React.lazy(() => import('./pages/PipelineExecutions'));
const UserAdmin = React.lazy(() => import('./pages/UserAdmin'));
const AuditLogs = React.lazy(() => import('./pages/AuditLogs'));
const DataQuality = React.lazy(() => import('./pages/DataQuality'));
const DataManagement = React.lazy(() => import('./pages/DataManagement'));
const Login = React.lazy(() => import('./pages/Login'));
const CreateOrder = React.lazy(() => import('./pages/CreateOrder'));
const OrderDetail = React.lazy(() => import('./pages/OrderDetail'));
const SpecExtractionPage = React.lazy(() => import('./pages/SpecExtractionPage'));
const MLExperimentationStudio = React.lazy(() => import('./components/NeuronStudio/MLExperimentationStudio/index'));
const NeuronIntelligenceStudio = React.lazy(() => import('./components/NeuronStudio/NeuronIntelligenceStudio'));
const AgenticAIStudio = React.lazy(() => import('./components/NeuronStudio/AgenticAIStudio/index'));
const UnifiedNeuronStudioShell = React.lazy(() => import('./components/NeuronStudio/UnifiedStudioShell'));
const PredictiveMaintenance = React.lazy(() => import('./pages/PredictiveMaintenance'));
const Diagrams = React.lazy(() => import('./pages/Diagrams'));
const DiagramEditor = React.lazy(() => import('./pages/DiagramEditor'));

// Sales & Revenue Intelligence — shell + lazy sub-product pages
const SalesRevenueShell = React.lazy(() => import('./pages/SalesRevenue/SalesRevenueShell').then(m => ({ default: m.SalesRevenueShell })));
const SRRevenueDashboard = React.lazy(() => import('./pages/SalesRevenue/RevenueDashboard').then(m => ({ default: m.RevenueDashboard })));
const SRCustomer360 = React.lazy(() => import('./pages/SalesRevenue/Customer360').then(m => ({ default: m.Customer360 })));
const SRProfitCubeStudio = React.lazy(() => import('./pages/SalesRevenue/ProfitCubeStudio').then(m => ({ default: m.ProfitCubeStudio })));
const SRWhatIfSimulator = React.lazy(() => import('./pages/SalesRevenue/WhatIfSimulator').then(m => ({ default: m.WhatIfSimulator })));
const SRAIForecasting = React.lazy(() => import('./pages/SalesRevenue/AIForecasting').then(m => ({ default: m.AIForecasting })));
const SRMarketSignal = React.lazy(() => import('./pages/SalesRevenue/MarketSignal').then(m => ({ default: m.MarketSignal })));
const SRDualArchitecture = React.lazy(() => import('./pages/SalesRevenue/DualArchitecture').then(m => ({ default: m.DualArchitecture })));

// Data Fusion — shell + lazy sub-product pages
const DataFusionShell = React.lazy(() => import('./pages/DataFusion/DataFusionShell').then(m => ({ default: m.DataFusionShell })));
const DataFusionAxonLanding = React.lazy(() => import('./pages/AxonLanding'));
const DataFusionAxonWorkspace = React.lazy(() => import('./pages/AxonWorkspace'));
const DataFusionConnectors = React.lazy(() => import('./pages/DataFusion/Connectors/ConnectorsHub').then(m => ({ default: m.ConnectorsHub })));
const DataFusionSpecParser = React.lazy(() => import('./pages/DataFusion/SpecParser/SpecParserStudio').then(m => ({ default: m.SpecParserStudio })));
const DataFusionExplorer = React.lazy(() => import('./pages/DataFusion/DataExplorer/DataExplorerPage').then(m => ({ default: m.DataExplorerPage })));
const DataFusionLineage = React.lazy(() => import('./pages/DataFusion/Lineage/LineageStudio').then(m => ({ default: m.LineageStudio })));
const DataFusionStorage = React.lazy(() => import('./pages/DataFusion/DeltaStorage/DeltaStorageStudio').then(m => ({ default: m.DeltaStorageStudio })));
const DataFusionSemantic = React.lazy(() => import('./pages/DataFusion/SemanticModeling/SemanticModelingPage').then(m => ({ default: m.SemanticModelingPage })));
const DataFusionTensor = React.lazy(() => import('./pages/DataFusion/TensorEngine/TensorEngineDashboard').then(m => ({ default: m.TensorEngineDashboard })));

// MRP (Material Requirements Planning) — new shell + lazy pages
const MRPShell = React.lazy(() => import('./pages/MRP/MRPShell').then(m => ({ default: m.MRPShell })));
const MRPDashboard = React.lazy(() => import('./pages/MRP/MRPDashboard').then(m => ({ default: m.MRPDashboard })));

// Orders
const SalesOrderList = React.lazy(() => import('./pages/MRP/Orders/SalesOrderList').then(m => ({ default: m.SalesOrderList })));
const CreateSalesOrder = React.lazy(() => import('./pages/MRP').then(m => ({ default: m.CreateSalesOrder })));
const SalesOrderDetail = React.lazy(() => import('./pages/MRP').then(m => ({ default: m.SalesOrderDetail })));
const ProductionOrderList = React.lazy(() => import('./pages/MRP/Production/ProductionOrderList').then(m => ({ default: m.ProductionOrderList })));
const CreateProductionOrder = React.lazy(() => import('./pages/MRP/Production').then(m => ({ default: m.CreateProductionOrder })));
const ProductionOrderDetail = React.lazy(() => import('./pages/MRP/Production').then(m => ({ default: m.ProductionOrderDetail })));
const PurchaseOrderList = React.lazy(() => import('./pages/MRP/Purchasing/PurchaseOrderListEnhanced').then(m => ({ default: m.PurchaseOrderListEnhanced })));
const CreatePurchaseOrder = React.lazy(() => import('./pages/MRP/Purchasing').then(m => ({ default: m.CreatePurchaseOrder })));
const CreateBulkPurchaseOrder = React.lazy(() => import('./pages/MRP/Purchasing').then(m => ({ default: m.CreateBulkPurchaseOrder })));
const PurchaseOrderDetail = React.lazy(() => import('./pages/MRP/Purchasing').then(m => ({ default: m.PurchaseOrderDetail })));
const ReceiveMaterials = React.lazy(() => import('./pages/MRP/Purchasing').then(m => ({ default: m.ReceiveMaterials })));

// Production
const ProductionScheduling = React.lazy(() => import('./pages/MRP/ProductionScheduling').then(m => ({ default: m.ProductionScheduling })));
const OperatorDashboard = React.lazy(() => import('./pages/MRP/ShopFloor').then(m => ({ default: m.OperatorDashboard })));
const ProductionControlPanel = React.lazy(() => import('./pages/MRP/ProductionControl').then(m => ({ default: m.ProductionControlPanel })));
const BOMManagement = React.lazy(() => import('./pages/MRP/BOMManagementEnhanced').then(m => ({ default: m.BOMManagementEnhanced })));

// Planning
const DemandPlanning = React.lazy(() => import('./pages/MRP/DemandPlanning').then(m => ({ default: m.DemandPlanning })));
const PurchasePlanning = React.lazy(() => import('./pages/MRP/PurchasePlanning').then(m => ({ default: m.PurchasePlanning })));
const BOMStudioContainer = React.lazy(() => import('./pages/MRP/BOMStudio/BOMStudioContainer').then(m => ({ default: m.BOMStudioContainer })));

// Inventory & Quality
const InventoryManagement = React.lazy(() => import('./pages/MRP/InventoryManagementEnhanced').then(m => ({ default: m.InventoryManagementEnhanced })));
const QualityDashboard = React.lazy(() => import('./pages/MRP/Quality/QualityDashboard').then(m => ({ default: m.QualityDashboard })));
const QualityInspections = React.lazy(() => import('./pages/MRP/Quality').then(m => ({ default: m.QualityInspections })));
const InspectionForm = React.lazy(() => import('./pages/MRP/Quality').then(m => ({ default: m.InspectionForm })));
const InspectionDetail = React.lazy(() => import('./pages/MRP/Quality').then(m => ({ default: m.InspectionDetail })));

// Supply Chain & Assets
const SupplyChainDashboard = React.lazy(() => import('./pages/MRP/SupplyChain/SupplyChainDashboard').then(m => ({ default: m.SupplyChainDashboard })));
const SupplyChainNetwork = React.lazy(() => import('./pages/MRP/Network/SupplyChainNetwork').then(m => ({ default: m.SupplyChainNetwork })));
const UnifiedMachineDashboard = React.lazy(() => import('./pages/MRP/Assets').then(m => ({ default: m.UnifiedMachineDashboard })));

// Partners
const CustomerList = React.lazy(() => import('./pages/MRP/Customers').then(m => ({ default: m.CustomerList })));
const CustomerDetail = React.lazy(() => import('./pages/MRP/Customers').then(m => ({ default: m.CustomerDetail })));
const SupplierList = React.lazy(() => import('./pages/MRP/Suppliers').then(m => ({ default: m.SupplierList })));
const SupplierDetail = React.lazy(() => import('./pages/MRP/Suppliers').then(m => ({ default: m.SupplierDetail })));

// Engineering / Specs
const ProductDetail = React.lazy(() => import('./pages/MRP/Products').then(m => ({ default: m.ProductDetail })));
const MaterialDetail = React.lazy(() => import('./pages/MRP/Materials').then(m => ({ default: m.MaterialDetail })));
const ProductSpecsView = React.lazy(() => import('./pages/MRP/ProductSpecs/ProductSpecsView').then(m => ({ default: m.ProductSpecsView })));
const MaterialSpecsView = React.lazy(() => import('./pages/MRP/Materials/MaterialSpecsView').then(m => ({ default: m.MaterialSpecsView })));

// Roles
const ProductionManagerHome = React.lazy(() => import('./pages/MRP/Roles').then(m => ({ default: m.ProductionManagerHome })));
const SalesManagerHome = React.lazy(() => import('./pages/MRP/Roles').then(m => ({ default: m.SalesManagerHome })));
const PurchasingManagerHome = React.lazy(() => import('./pages/MRP/Roles').then(m => ({ default: m.PurchasingManagerHome })));

// Legacy container (kept for any stale links)
const MRPContainer = React.lazy(() => import('./pages/MRP').then(m => ({ default: m.MRPContainer })));

// Product Data Sheets (public internal catalog + per-sheet print route used by Puppeteer)
const DataSheetsIndex = React.lazy(() => import('./pages/DataSheets/index'));
const SheetPage = React.lazy(() => import('./pages/DataSheets/SheetPage'));
const PlatformHome = React.lazy(() => import('./pages/PlatformHome'));

// Router component to redirect demo and sales_manager users; others see platform hub
const HomeRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'demo') {
    return <Navigate to="/sales-dashboard" replace />;
  }
  if (user?.role === 'sales_manager') {
    return <Navigate to="/sales-intelligence" replace />;
  }
  return <PlatformHome />;
};

function LegacyAxonWorkspaceRedirect() {
  const { id } = useParams();
  return <Navigate to={`/data-fusion/axon/workspace/${id ?? ''}`} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

function App() {
  // Initialize widget registry on app startup
  useEffect(() => {
    console.log('🚀 Initializing widget registry...');
    try {
      console.log('✅ Widget registry initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize widget registry:', error);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContextProvider>
        <AuthProvider>
          <Router>
            <CssBaseline />
            <Routes>
              <Route path="/login" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Login />
                </Suspense>
              } />
              {/* Product Data Sheets live outside the protected Layout so
                  Puppeteer can render them without auth. */}
              <Route path="/data-sheets" element={
                <Suspense fallback={<LoadingFallback />}>
                  <DataSheetsIndex />
                </Suspense>
              } />
              <Route path="/data-sheets/:slug" element={
                <Suspense fallback={<LoadingFallback />}>
                  <SheetPage />
                </Suspense>
              } />
              <Route path="/*" element={
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route path="/" element={<ProtectedRoute><HomeRouter /></ProtectedRoute>} />
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/orders" element={<ProtectedRoute><SalesManagerRoute><Orders /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/orders/create" element={<ProtectedRoute><SalesManagerRoute><CreateOrder /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/orders/:id" element={<ProtectedRoute><SalesManagerRoute><OrderDetail /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/customers" element={<ProtectedRoute><SalesManagerRoute><Customers /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/products" element={<ProtectedRoute><SalesManagerRoute><Products /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/etl" element={<ProtectedRoute><SalesManagerRoute><EtlPipelines /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/etl/builder" element={<ProtectedRoute><SalesManagerRoute><PipelineBuilder /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/etl/builder/:id" element={<ProtectedRoute><SalesManagerRoute><PipelineBuilder /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/etl/executions" element={<ProtectedRoute><SalesManagerRoute><PipelineExecutions /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/etl/executions/:id" element={<ProtectedRoute><SalesManagerRoute><PipelineExecutions /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/admin/users" element={<ProtectedRoute><SalesManagerRoute><UserAdmin /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/audit" element={<ProtectedRoute><SalesManagerRoute><AuditLogs /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/data-quality" element={<ProtectedRoute><SalesManagerRoute><DataQuality /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/data-management" element={<ProtectedRoute><SalesManagerRoute><DataManagement /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/predictive-maintenance" element={<ProtectedRoute><PredictiveMaintenance /></ProtectedRoute>} />
                      <Route path="/diagrams" element={<ProtectedRoute><SalesManagerRoute><Diagrams /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/diagrams/create" element={<ProtectedRoute><SalesManagerRoute><DiagramEditor /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/diagrams/edit/:id" element={<ProtectedRoute><SalesManagerRoute><DiagramEditor /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/spec-extraction" element={<ProtectedRoute><SalesManagerRoute><SpecExtractionPage /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/ml-studio" element={<ProtectedRoute><SalesManagerRoute><MLExperimentationStudio /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/neuron-intelligence" element={<ProtectedRoute><SalesManagerRoute><NeuronIntelligenceStudio /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/agentic-ai" element={<ProtectedRoute><SalesManagerRoute><AgenticAIStudio /></SalesManagerRoute></ProtectedRoute>} />
                      <Route path="/neuron-studio" element={<ProtectedRoute><SalesManagerRoute><UnifiedNeuronStudioShell /></SalesManagerRoute></ProtectedRoute>} />
                      
                      {/* ── Sales & Revenue — unified shell with 7 sub-products ── */}
                      <Route path="/sales-revenue" element={
                        <ProtectedRoute><SalesManagerRoute><SalesRevenueShell /></SalesManagerRoute></ProtectedRoute>
                      }>
                        <Route index element={<Box />} />
                        <Route path="dashboard" element={<SRRevenueDashboard />} />
                        <Route path="customers" element={<SRCustomer360 />} />
                        <Route path="customers/:id" element={<SRCustomer360 />} />
                        <Route path="profit-cube" element={<SRProfitCubeStudio />} />
                        <Route path="what-if" element={<SRWhatIfSimulator />} />
                        <Route path="forecast" element={<SRAIForecasting />} />
                        <Route path="market" element={<SRMarketSignal />} />
                        <Route path="architecture" element={<SRDualArchitecture />} />
                      </Route>

                      {/* Legacy flat routes → redirect into Sales Revenue shell */}
                      <Route path="/sales-dashboard" element={<Navigate to="/sales-revenue/dashboard" replace />} />
                      <Route path="/sales-intelligence" element={<Navigate to="/sales-revenue/market" replace />} />

                      {/* ── Data Fusion — unified shell with 8 sub-products ── */}
                      <Route path="/data-fusion" element={
                        <ProtectedRoute><SalesManagerRoute><DataFusionShell /></SalesManagerRoute></ProtectedRoute>
                      }>
                        <Route index element={<Box />} />
                        <Route path="axon" element={<DataFusionAxonLanding />} />
                        <Route path="axon/workspace/:id" element={<DataFusionAxonWorkspace />} />
                        <Route path="connectors" element={<DataFusionConnectors />} />
                        <Route path="spec-parser" element={<DataFusionSpecParser />} />
                        <Route path="explorer" element={<DataFusionExplorer />} />
                        <Route path="lineage" element={<DataFusionLineage />} />
                        <Route path="storage" element={<DataFusionStorage />} />
                        <Route path="semantic" element={<DataFusionSemantic />} />
                        <Route path="tensor" element={<DataFusionTensor />} />
                      </Route>

                      {/* Legacy flat routes → redirect into Data Fusion shell */}
                      <Route path="/axon" element={<Navigate to="/data-fusion/axon" replace />} />
                      <Route path="/axon/workspace/:id" element={<LegacyAxonWorkspaceRedirect />} />
                      <Route path="/data-explorer" element={<Navigate to="/data-fusion/explorer" replace />} />
                      <Route path="/data-lineage" element={<Navigate to="/data-fusion/lineage" replace />} />
                      <Route path="/semantic-modeling" element={<Navigate to="/data-fusion/semantic" replace />} />
                      <Route path="/api-integration" element={<Navigate to="/data-fusion/connectors" replace />} />
                      <Route path="/schema-management" element={<Navigate to="/data-fusion/storage" replace />} />

                      {/* ── MRP System — nested sidebar navigation ── */}
                      <Route path="/mrp" element={
                        <ProtectedRoute><SalesManagerRoute><MRPShell /></SalesManagerRoute></ProtectedRoute>
                      }>
                        {/* Index → redirect to dashboard */}
                        <Route index element={<Navigate to="/mrp/dashboard" replace />} />

                        {/* Dashboard */}
                        <Route path="dashboard" element={<MRPDashboard />} />

                        {/* Roles */}
                        <Route path="roles/production" element={<ProductionManagerHome />} />
                        <Route path="roles/sales" element={<SalesManagerHome />} />
                        <Route path="roles/purchasing" element={<PurchasingManagerHome />} />

                        {/* Orders */}
                        <Route path="orders/sales" element={<SalesOrderList />} />
                        <Route path="orders/sales/create" element={<CreateSalesOrder />} />
                        <Route path="orders/sales/:id" element={<SalesOrderDetail />} />
                        <Route path="orders/production" element={<ProductionOrderList />} />
                        <Route path="orders/production/create" element={<CreateProductionOrder />} />
                        <Route path="orders/production/:id" element={<ProductionOrderDetail />} />
                        <Route path="orders/purchasing" element={<PurchaseOrderList />} />
                        <Route path="orders/purchasing/create" element={<CreatePurchaseOrder />} />
                        <Route path="orders/purchasing/create-bulk" element={<CreateBulkPurchaseOrder />} />
                        <Route path="orders/purchasing/:id" element={<PurchaseOrderDetail />} />
                        <Route path="orders/purchasing/:id/receive" element={<ReceiveMaterials />} />

                        {/* Production */}
                        <Route path="production/scheduling" element={<ProductionScheduling />} />
                        <Route path="production/shop-floor" element={<OperatorDashboard />} />
                        <Route path="production/control" element={<ProductionControlPanel />} />
                        <Route path="production/boms" element={<BOMManagement />} />

                        {/* Planning */}
                        <Route path="planning/demand" element={<DemandPlanning />} />
                        <Route path="planning/purchasing" element={<PurchasePlanning />} />
                        <Route path="planning/bom-studio" element={<BOMStudioContainer />} />

                        {/* Inventory & Quality */}
                        <Route path="inventory" element={<InventoryManagement />} />
                        <Route path="quality" element={<QualityDashboard />} />
                        <Route path="quality/inspections" element={<QualityInspections />} />
                        <Route path="quality/inspections/create" element={<InspectionForm />} />
                        <Route path="quality/inspections/:id" element={<InspectionDetail />} />

                        {/* Supply Chain */}
                        <Route path="supply-chain" element={<SupplyChainDashboard />} />
                        <Route path="supply-chain/network" element={<SupplyChainNetwork />} />

                        {/* Assets */}
                        <Route path="assets" element={<UnifiedMachineDashboard />} />

                        {/* Partners */}
                        <Route path="partners/customers" element={<CustomerList />} />
                        <Route path="partners/customers/:id" element={<CustomerDetail />} />
                        <Route path="partners/suppliers" element={<SupplierList />} />
                        <Route path="partners/suppliers/:id" element={<SupplierDetail />} />

                        {/* Engineering / Specs */}
                        <Route path="engineering/product-specs" element={<ProductSpecsView />} />
                        <Route path="engineering/material-specs" element={<MaterialSpecsView />} />
                        <Route path="engineering/products/:id" element={<ProductDetail />} />
                        <Route path="engineering/materials/:id" element={<MaterialDetail />} />

                        {/* Legacy deep-link aliases (backward compat) */}
                        <Route path="products/:id" element={<ProductDetail />} />
                        <Route path="materials/:id" element={<MaterialDetail />} />
                        <Route path="customers" element={<CustomerList />} />
                        <Route path="customers/:id" element={<CustomerDetail />} />
                        <Route path="suppliers" element={<SupplierList />} />
                        <Route path="suppliers/:id" element={<SupplierDetail />} />
                        <Route path="pm-home" element={<ProductionManagerHome />} />
                        <Route path="sm-home" element={<SalesManagerHome />} />
                        <Route path="pur-home" element={<PurchasingManagerHome />} />
                        <Route path="production-control" element={<ProductionControlPanel />} />
                        <Route path="bom-studio" element={<BOMStudioContainer />} />
                        <Route path="shop-floor" element={<OperatorDashboard />} />
                        <Route path="assets/machines" element={<UnifiedMachineDashboard />} />
                      </Route>

                      {/* Legacy flat /mrp route → redirect into new shell */}
                      <Route path="/mrp-legacy" element={<ProtectedRoute><SalesManagerRoute><MRPContainer /></SalesManagerRoute></ProtectedRoute>} />
                    </Routes>
                  </Suspense>
                </Layout>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeContextProvider>
    </QueryClientProvider>
  );
}

export default App; 